/**
 * Webhook Capture Card
 *
 * Arms a single-use capture window on the order webhook so a test order sent from a
 * restaurant that is still being onboarded is recorded rather than 404'd. The
 * organisation_id only appears inside the webhook payload, so a test order is the
 * cheapest way to obtain it without sourcing it by hand.
 *
 * No webhook URL needs changing: CloudWaitress is already pointed at /webhook/pumpd by
 * the onboarding automation, and the capture runs off that handler's fallback path.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/api/client';
import { Loader2, Radio, CheckCircle2, ShieldCheck, ShieldAlert, X } from 'lucide-react';

// Matches CAPTURE_FIELDS in server/handlers/admin/admin-onboarding-capture-handler.js
interface WebhookCapture {
  id: string;
  user_id: string;
  status: 'armed' | 'captured' | 'confirmed' | 'discarded' | 'cancelled' | 'expired';
  expires_at: string;
  captured_at: string | null;
  organisation_id: string | null;
  restaurant_id: string | null;
  restaurant_name: string | null;
  order_number: string | null;
  event_type: string | null;
  secret_verified: boolean;
}

interface WebhookCaptureCardProps {
  userId: string;
  /** Pushes confirmed values back into the tab's form so a later save can't overwrite them */
  onConfirmed: (values: { organisation_id: string; restaurant_id?: string }) => void;
}

const POLL_INTERVAL_MS = 3000;

const WebhookCaptureCard: React.FC<WebhookCaptureCardProps> = ({ userId, onConfirmed }) => {
  const [capture, setCapture] = useState<WebhookCapture | null>(null);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isArmed = capture?.status === 'armed';
  const isCaptured = capture?.status === 'captured';

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/admin/onboarding/webhook-capture/status?user_id=${userId}`);
      setCapture(data.capture || null);
    } catch (error) {
      console.error('Error loading webhook capture status:', error);
    }
  }, [userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll only while a window is open — a captured or resolved window is terminal
  useEffect(() => {
    if (!isArmed) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = setInterval(loadStatus, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isArmed, loadStatus]);

  // Countdown against the server-issued expiry
  useEffect(() => {
    if (!isArmed || !capture) {
      setSecondsLeft(0);
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.round((new Date(capture.expires_at).getTime() - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isArmed, capture]);

  const handleArm = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post('/api/admin/onboarding/webhook-capture/arm', { user_id: userId });
      setCapture(data.capture);

      toast({
        title: 'Capture window open',
        description: data.has_webhook_secret
          ? 'Send a test order through the restaurant now.'
          : 'No webhook secret on this record yet — the capture cannot be verified.'
      });
    } catch (error) {
      console.error('Error arming webhook capture:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to open capture window',
        variant: 'destructive'
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!capture) return;
    setBusy(true);
    try {
      await apiClient.post('/api/admin/onboarding/webhook-capture/cancel', { capture_id: capture.id });
      await loadStatus();
    } catch (error) {
      console.error('Error cancelling webhook capture:', error);
      toast({
        title: 'Error',
        description: 'Failed to close capture window',
        variant: 'destructive'
      });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!capture) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post('/api/admin/onboarding/webhook-capture/confirm', { capture_id: capture.id });
      setCapture(data.capture);

      onConfirmed({
        organisation_id: data.applied.organisation_id,
        restaurant_id: data.applied.restaurant_id
      });

      toast({
        title: 'Saved',
        description: 'Organisation ID applied to the onboarding record'
      });
    } catch (error) {
      console.error('Error confirming webhook capture:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to confirm capture',
        variant: 'destructive'
      });
    } finally {
      setBusy(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h5 className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <Radio className="h-4 w-4" />
            Capture IDs from a test order
          </h5>
          <p className="mt-1 text-xs text-slate-600">
            Opens a single-use window on the order webhook. Send a test order through the
            restaurant and the organisation ID will be read from the payload — no webhook
            URL changes needed.
          </p>
        </div>

        {!isArmed && !isCaptured && (
          <Button size="sm" variant="outline" onClick={handleArm} disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Radio className="mr-2 h-4 w-4" />}
            Capture Webhook Payload
          </Button>
        )}
      </div>

      {isArmed && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md bg-blue-50 p-3">
          <div className="flex items-center gap-2 text-sm text-blue-900">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Waiting for a test order…</span>
            <Badge variant="outline" className="font-mono">{formatCountdown(secondsLeft)}</Badge>
          </div>
          <Button size="sm" variant="ghost" onClick={handleCancel} disabled={busy}>
            <X className="mr-1 h-4 w-4" />
            Cancel
          </Button>
        </div>
      )}

      {isCaptured && (
        <div className="mt-3 space-y-3 rounded-md bg-green-50 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-green-900">
            <CheckCircle2 className="h-4 w-4" />
            Payload captured
            {capture?.secret_verified ? (
              <Badge variant="outline" className="gap-1 border-green-600 text-green-700">
                <ShieldCheck className="h-3 w-3" />
                Secret verified
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 border-yellow-600 text-yellow-700">
                <ShieldAlert className="h-3 w-3" />
                Unverified
              </Badge>
            )}
          </div>

          {!capture?.secret_verified && (
            <p className="text-xs text-yellow-800">
              No webhook secret was set on this onboarding record, so the payload could not
              be authenticated. Confirm the values look right before saving.
            </p>
          )}

          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-slate-600">Organisation ID</dt>
              <dd className="font-mono text-slate-900">{capture?.organisation_id || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-600">Restaurant ID</dt>
              <dd className="font-mono text-slate-900">{capture?.restaurant_id || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-600">Restaurant name</dt>
              <dd className="text-slate-900">{capture?.restaurant_name || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-600">Order</dt>
              <dd className="text-slate-900">
                {capture?.order_number ? `#${capture.order_number}` : '—'}
                {capture?.event_type ? ` (${capture.event_type})` : ''}
              </dd>
            </div>
          </dl>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleConfirm} disabled={busy || !capture?.organisation_id}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Confirm &amp; Save
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel} disabled={busy}>
              Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookCaptureCard;
